/**
 * Dashboard page - Projects landing page
 * Story 226: Projects Dashboard
 * Story 287: Add project by URL flow
 * Story 356: Web event instrumentation
 * Story 464: Project export
 * Story 586: Connect-your-agent step for people with no projects
 */

import { useEffect, useState, useCallback } from 'react';
import { track } from '../lib/analytics';
import { useNavigate } from 'react-router-dom';
import { config } from '../config';
import { useProjects } from '../hooks/useProjects';
import { useProjectExport } from '../hooks/useProjectExport';
import { ViewToggle, type ViewMode } from '../components/dashboard/ViewToggle';
import { ProjectsGrid } from '../components/dashboard/ProjectsGrid';
import { ProjectsList } from '../components/dashboard/ProjectsList';
import { ProjectsEmptyState } from '../components/dashboard/ProjectsEmptyState';
import { InaccessibleSection } from '../components/dashboard/InaccessibleSection';
import { AddProjectModal } from '../components/dashboard/AddProjectModal';
import { ConnectAgentModal } from '../components/dashboard/ConnectAgentModal';
import { isConnectStepFinished, markConnectStepFinished } from '../lib/connectStep';
import { ExportModal } from '../components/dashboard/ExportModal';
import { ButterBar } from '../components/editor/ButterBar';
import { ThemeSwitcher } from '../components/ThemeSwitcher';
import './Dashboard.css';

const VIEW_MODE_KEY = 'dashboard_view_mode';

interface UserInfo {
  githubUsername: string;
  email: string;
}

export function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const stored = localStorage.getItem(VIEW_MODE_KEY);
    return (stored === 'list' || stored === 'grid') ? stored : 'list';
  });
  const [showAddProject, setShowAddProject] = useState(false);
  const [showConnectStep, setShowConnectStep] = useState(false);

  const { data, isLoading, error, refetch } = useProjects();
  const {
    startExport,
    isExporting,
    exportReady,
    exportError,
    download,
    dismiss,
    autoDismissMs,
  } = useProjectExport();

  // Fetch user info
  useEffect(() => {
    async function fetchUser() {
      try {
        const response = await fetch(`${config.apiUrl}/auth/me`, {
          credentials: 'include',
        });
        const json = await response.json();
        if (json.authenticated) {
          setUser({
            githubUsername: json.githubUsername,
            email: json.email,
          });
        }
      } catch {
        // Ignore fetch errors
      }
    }
    fetchUser();
  }, []);

  // Persist view mode
  useEffect(() => {
    localStorage.setItem(VIEW_MODE_KEY, viewMode);
  }, [viewMode]);

  // Story 586: people with no projects first see how to connect their coding agent
  useEffect(() => {
    if (!isLoading && data) {
      const hasNoProjects =
        data.accessible.length === 0 && data.inaccessible.length === 0;
      if (hasNoProjects && !isConnectStepFinished()) {
        setShowConnectStep(true);
      }
    }
  }, [isLoading, data]);

  const handleSignOut = useCallback(async () => {
    try {
      await fetch(`${config.apiUrl}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Logout may fail if session already expired - continue to redirect
    }
    window.location.href = '/';
  }, []);

  const handleSelectProject = useCallback(
    (projectSlug: string) => {
      // Story 356: Track project selection
      track({
        name: 'project_switched',
        properties: { to_project: projectSlug },
      });
      // Navigate to workspace with selected project
      navigate(`/workspace?project=${encodeURIComponent(projectSlug)}`);
    },
    [navigate]
  );

  const handleOpenAddProject = useCallback(() => {
    setShowAddProject(true);
  }, []);

  const handleCloseAddProject = useCallback(() => {
    setShowAddProject(false);
  }, []);

  const handleCloseConnectStep = useCallback(() => {
    markConnectStepFinished();
    setShowConnectStep(false);
  }, []);

  const handleConnectStepAddRepository = useCallback(() => {
    markConnectStepFinished();
    setShowConnectStep(false);
    setShowAddProject(true);
  }, []);

  const handleProjectAdded = useCallback(() => {
    refetch();
  }, [refetch]);

  const isEmpty =
    !isLoading &&
    data &&
    data.accessible.length === 0 &&
    data.inaccessible.length === 0;

  return (
    <div className="dashboard">
      <nav className="dashboard__nav">
        <div className="dashboard__nav-inner">
          <div className="dashboard__nav-brand">
            <a href="/" className="dashboard__logo-link">
              <img
                src="/ceetrix-green.png"
                alt="Ceetrix"
                className="dashboard__logo"
              />
            </a>
          </div>
          <div className="dashboard__nav-actions">
            {user && (
              <a
                href={`https://github.com/${user.githubUsername}`}
                target="_blank"
                rel="noopener noreferrer"
                className="dashboard__username"
              >
                <svg viewBox="0 0 16 16" fill="currentColor" className="dashboard__github-icon">
                  <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"/>
                </svg>
                @{user.githubUsername}
              </a>
            )}
            <ThemeSwitcher />
            <button
              onClick={handleSignOut}
              className="dashboard__sign-out"
              type="button"
            >
              Sign Out
            </button>
          </div>
        </div>
      </nav>

      <main className="dashboard__main">
        <div className="dashboard__header">
          <h2 className="dashboard__title">Projects</h2>
          <div className="dashboard__actions">
            {!isEmpty && (
              <>
                <ViewToggle value={viewMode} onChange={setViewMode} />
                <button
                  className="dashboard__new-project"
                  onClick={handleOpenAddProject}
                  type="button"
                >
                  Add Project...
                </button>
              </>
            )}
          </div>
        </div>

        {isLoading && (
          <div className="dashboard__loading">Loading projects...</div>
        )}

        {error && (
          <div className="dashboard__error">
            Failed to load projects. Please try again.
          </div>
        )}

        {isEmpty && <ProjectsEmptyState onCreateProject={handleOpenAddProject} />}

        {!isLoading && !error && data && data.accessible.length > 0 && (
          <>
            {viewMode === 'grid' ? (
              <ProjectsGrid
                projects={data.accessible}
                onSelectProject={handleSelectProject}
                onExport={startExport}
              />
            ) : (
              <ProjectsList
                projects={data.accessible}
                onSelectProject={handleSelectProject}
                onExport={startExport}
              />
            )}
          </>
        )}

        {!isLoading && !error && data && data.inaccessible.length > 0 && (
          <InaccessibleSection projects={data.inaccessible} />
        )}
      </main>

      {showConnectStep && (
        <ConnectAgentModal
          onClose={handleCloseConnectStep}
          onAddRepository={handleConnectStepAddRepository}
        />
      )}

      {showAddProject && (
        <AddProjectModal
          onClose={handleCloseAddProject}
          onSuccess={handleProjectAdded}
        />
      )}

      {isExporting && <ExportModal onCancel={dismiss} />}

      {exportReady && (
        <ButterBar
          message="Export ready!"
          actionLabel="Download"
          onAction={download}
          onDismiss={dismiss}
          autoDismissMs={autoDismissMs}
        />
      )}

      {exportError && (
        <ButterBar
          message={`Export failed: ${exportError}`}
          onDismiss={dismiss}
          autoDismissMs={autoDismissMs}
        />
      )}
    </div>
  );
}
